import { useContext, useEffect, useRef, useState } from "react";
import { ZoomContext } from "@/zoom/zoom-context";
import { SPEAKING_DECAY_MS, SPEAKING_SWEEP_MS } from "../telehealth-v2-constant";

/**
 * userIds the SDK currently reports as talking. Multiple people can speak at
 * once — `active-speaker` delivers a volume-ordered array, and every id in it
 * is held for SPEAKING_DECAY_MS so brief gaps between words don't flicker.
 */
export function useActiveSpeakers(): ReadonlySet<number> {
  const zmClient = useContext(ZoomContext);
  const [speakingIds, setSpeakingIds] = useState<ReadonlySet<number>>(
    () => new Set<number>()
  );
  const lastHeardRef = useRef<Map<number, number>>(new Map());

  useEffect(() => {
    if (!zmClient) return;
    const onActiveSpeaker = (payload: any) => {
      const list = Array.isArray(payload) ? payload : [payload].filter(Boolean);
      const now = Date.now();
      for (const speaker of list) {
        const id = Number(speaker?.userId);
        if (Number.isFinite(id)) lastHeardRef.current.set(id, now);
      }
    };
    zmClient.on("active-speaker", onActiveSpeaker);
    return () => {
      zmClient.off("active-speaker", onActiveSpeaker);
    };
  }, [zmClient]);

  useEffect(() => {
    const id = window.setInterval(() => {
      const cutoff = Date.now() - SPEAKING_DECAY_MS;
      const live = new Set<number>();
      for (const [userId, heardAt] of lastHeardRef.current) {
        if (heardAt >= cutoff) live.add(userId);
        else lastHeardRef.current.delete(userId);
      }
      setSpeakingIds((prev) => {
        if (prev.size === live.size && [...live].every((v) => prev.has(v))) {
          return prev;
        }
        return live;
      });
    }, SPEAKING_SWEEP_MS);
    return () => window.clearInterval(id);
  }, []);

  return speakingIds;
}
